import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { SSEProvider } from './hooks/useSSE';
import Layout from './components/Layout';
import { TierPortfolioProvider } from './hooks/useTierPortfolio';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AccountDetail from './pages/AccountDetail';
import Tickers from './pages/Tickers';
import Analysis from './pages/Analysis';
import PositionAnalysis from './pages/PositionAnalysis';
import PositionApprovals from './pages/PositionApprovals';
import Feeds from './pages/Feeds';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import MarketThesisPerformance from './pages/MarketThesisPerformance';
import OrchestrationAudits from './pages/OrchestrationAudits';
import AILogs from './pages/AILogs';
import NotificationAudits from './pages/NotificationAudits';
import MacroCockpit from './pages/MacroCockpit';
import MacroCycle from './pages/MacroCycle';
import MacroData from './pages/MacroData';
import LandingPage from './pages/LandingPage';
import LoadingSpinner from './components/LoadingSpinner';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <TierPortfolioProvider>
              <Layout />
            </TierPortfolioProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="accounts/:accountId" element={<AccountDetail />} />
        <Route path="tickers" element={<Tickers />} />
        <Route path="analysis" element={<Analysis />} />
        <Route path="position-analysis" element={<PositionAnalysis />} />
        <Route path="position-approvals" element={<PositionApprovals />} />
        <Route path="feeds" element={<Feeds />} />
        <Route
          path="market-thesis-performance"
          element={<MarketThesisPerformance />}
        />
        <Route path="orchestration-audits" element={<OrchestrationAudits />} />
        <Route path="ai-logs" element={<AILogs />} />
        <Route path="notification-audits" element={<NotificationAudits />} />
        <Route path="macro" element={<MacroCockpit />} />
        <Route path="macro/cycle" element={<MacroCycle />} />
        <Route path="macro/data" element={<MacroData />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={<Profile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const SSEWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setAuthToken(token);

    const handleStorageChange = () => {
      const newToken = localStorage.getItem('authToken');
      setAuthToken(newToken);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return <SSEProvider authToken={authToken}>{children}</SSEProvider>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <SSEWrapper>
        <Router basename={process.env.PUBLIC_URL || ''}>
          <AppRoutes />
        </Router>
      </SSEWrapper>
    </AuthProvider>
  );
};

export default App;
